import { useEffect, useRef } from 'react'
import { gsap, Power1 } from 'gsap'
import Link from 'next/link'
import styles from './Process.module.scss'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import TitleFlex from '../TitleFlex'

gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TweenVars = {
  y: '50',
  duration: 0.5,
  opacity: 0,
  ease: Power1.easeOut,
}

export interface ProcessItem {
  title: string
  term?: string
  text: string
  deliverables?: {
    link: string
    name: string
  }[]
  content?: {
    title: string
    text: string
    src?: string
    srcHeight?: string
    customElements?: JSX.Element
  }[]
}

interface Props {
  list: ProcessItem[]
  isVisibleAnnotation?: boolean
  leftElement?: JSX.Element
  sectionSuTitle?: string
  isBorderBottom?: boolean
}

const Process: React.FC<Props> = ({
  list,
  isVisibleAnnotation,
  leftElement,
  sectionSuTitle,
  isBorderBottom = false,
}) => {
  const timeline = gsap.timeline()
  const itemsRef = useRef<HTMLLIElement[]>([])
  const sectionTitle = {
    title: 'Our approach',
    sub_title: sectionSuTitle ? sectionSuTitle : 'サービス提供プロセス',
  }

  useEffect(() => {
    timeline.from('.Process_title', tweenVars, 0.3).from(itemsRef.current, {
      ...tweenVars,
      stagger: 0.3, // 各要素のアニメーションを0.3秒ずつ遅らせて実行
    })

    ScrollTrigger.create({
      animation: timeline,
      trigger: '.Process_container',
      start: 'top 90%',
      toggleActions: 'play none none reverse',
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      timeline.kill()
    }
  }, [])

  return (
    <section className={`${styles.wrapper} ${isBorderBottom ? styles.border_bottom : ''}`}>
      <div className={styles.inner}>
        <div>
          <TitleFlex sectionTitle={sectionTitle} adjustFill />
          {(leftElement && <>{leftElement}</>) || <></>}
        </div>
        <div>
          <ul className="Process_container">
            {list.map((item, index) => {
              const isLast = index === list.length - 1
              return (
                <li
                  key={index}
                  ref={(el) => {
                    if (el) itemsRef.current[index] = el
                  }}
                  className={styles.process_item}
                >
                  <div className={styles.item_container}>
                    <div className={`${styles.process_item_num} ${index + 1}`}>0{index + 1}</div>
                    <div className={styles.process_rights}>
                      <div className={styles.process_rights_head}>
                        <h4 className={styles.process_rights_head_title}>
                          <span>{item.title}</span>
                          <span className={styles.process_rights_head_title_terms}>{item.term}</span>
                        </h4>
                        <p className={`${styles.process_rights_head_detail} ${styles.process_rights_head_detail_last}`}>
                          {item.text}
                        </p>
                      </div>
                      {item.deliverables && (
                        <div className={styles.process_rights_bottom}>
                          <p className={styles.process_rights_bottom_text}>成果物</p>
                          <div className={styles.process_rights_bottom_items}>
                            {item.deliverables.map((deliverable, i) => (
                              <Link key={i} href={deliverable.link}>
                                <p className={styles.process_rights_bottom_items_link}>{deliverable.name}</p>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                      {item.content && (
                        <div className={styles.process_rights_contents}>
                          {item.content.map((content, i) => {
                            if (content.customElements) {
                              return content.customElements
                            }
                            return (
                              <div key={i} className={styles.process_rights_contents_content}>
                                <p className={styles.process_rights_contents_content_title}>{content.title}</p>
                                <p className={styles.process_rights_contents_content_text}>{content.text}</p>
                                {content.src && (
                                  <img
                                    src={content.src}
                                    alt="process"
                                    className={styles.process_rights_contents_content_img}
                                    style={{
                                      height: content.srcHeight ? `${content.srcHeight} !important` : '',
                                      objectFit: content.srcHeight ? `contain` : 'cover',
                                    }}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                      {isLast && isVisibleAnnotation && (
                        <p className={styles.annotation_text}>
                          ※当内容をベースに、お客さまと協議の上、実際のプロジェクト範囲とタスク、期間を定義して進めます。
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default Process
